import { useCallback, useMemo, useState } from 'react';
import { AppButton } from 'uikit/buttons';
import { Tooltip } from 'uikit/tooltip/tooltip.component';
import { ReactComponent as Trigon } from '../../assets/img/common/trigon_filled_24.svg';
import Style from './errorSurveyScoreCalculation.module.scss';
import { TypographyStyles } from '../../styles';
import clsx from 'clsx';
import { logger } from '../../application/logging/logging';
import { QueryFactory } from '../../services/api';
import { useQueryClient } from '@tanstack/react-query';
import { StudyRoleClaimValue } from 'src/services/api/api-client';
import { useScopedTranslation } from '../../application/localisation/useScopedTranslation';
import { useHasStudyClaims } from 'src/helpers/auth/auth-helper';

export type ErrorScoreCalculationProps = {
  canRetry?: boolean;
  onClick?: (e: any) => void;
  isInProcess?: boolean;
  answerId: number | null;
};

export const ErrorSurveyScoreCalculation = (props: ErrorScoreCalculationProps) => {
  const { onClick, isInProcess, canRetry } = props;
  const { t } = useScopedTranslation('Forms.Score.ScoreCalculation');
  const { hasStudyClaim } = useHasStudyClaims();
  const queryClient = useQueryClient();
  const [retrying, setRetrying] = useState(false);

  const retryHandler = useCallback(async () => {
    setRetrying(true);

    try {
      await QueryFactory.SurveyQuery.Client.recalculateScore(props.answerId!);

      await Promise.all([
        queryClient.invalidateQueries(QueryFactory.SurveyQuery.getQuestionnairesQueryKey()),
        queryClient.invalidateQueries(QueryFactory.SurveyQuery.getLastSurveysStatisticsQueryKey()),
        queryClient.invalidateQueries(QueryFactory.PatientQuery.getPatientsQueryKey()),
        queryClient.invalidateQueries(QueryFactory.SurveyQuery.getAnswerStatisticQueryKey(props.answerId!)),
        queryClient.invalidateQueries(QueryFactory.SurveyQuery.getAnswersStatisticQueryKey()),
      ]);
    } catch (error: any) {
      logger().error('Survey score calculation is failed', error);
    }

    setRetrying(false);
    return;
  }, [props, queryClient]);

  const tooltipContent = useMemo(() => {
    return (
      <div className={Style.container}>
        <div className={TypographyStyles.plainText12}>{canRetry ? t('ErrorCalculation') : t('CantRetryMessage')}</div>
        {isInProcess ? (
          <div className={clsx(TypographyStyles.plainText12, Style.inProgressText)}>{t('InProgress')}</div>
        ) : (
          canRetry &&
          hasStudyClaim(StudyRoleClaimValue.PatientCreate) && (
            <AppButton
              variant={'icon-link'}
              colorSchema={'primary'}
              text={t('Retry')}
              onClick={retryHandler}
              disabled={retrying}
              isLoading={isInProcess}
              textClassName={TypographyStyles.plainText12}
            />
          )
        )}
      </div>
    );
  }, [canRetry, hasStudyClaim, isInProcess, retryHandler, retrying, t]);

  return (
    <Tooltip tooltipContent={tooltipContent}>
      <Trigon className={clsx(Style.redIcon, { [Style.cursorPointer]: !!onClick })} onClick={onClick} />
    </Tooltip>
  );
};
